import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Divider,
  Alert,
  useTheme,
  alpha,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { verifyRootAction2FA } from "../../contexts/services/admin/securityServices";

const DEFAULT_VERIFY = async (verificationCode) => {
  try {
    await verifyRootAction2FA(verificationCode);
    return true;
  } catch (error) {
    return false;
  }
};

const SecurityGuard = ({
  open,
  onClose,
  onPassed,
  details,
  verifyCode = DEFAULT_VERIFY,
  requireSixDigits = true,
}) => {
  const theme = useTheme();

  const [verificationCode, setVerificationCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorText, setErrorText] = useState("");

  useEffect(() => {
    if (open) {
      setVerificationCode("");
      setErrorText("");
      setSubmitting(false);
    }
  }, [open]);

  const trimmedCode = verificationCode.trim();
  const isSixDigits = /^\d{6}$/.test(trimmedCode);
  const canSubmit = useMemo(() => {
    if (submitting) return false;
    if (!trimmedCode) return false;
    if (requireSixDigits && !isSixDigits) return false;
    return true;
  }, [submitting, trimmedCode, requireSixDigits, isSixDigits]);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setErrorText("");
    try {
      const allowed = await verifyCode(trimmedCode);
      if (!allowed) {
        setErrorText("Verification failed. Please try again.");
        return;
      }
      await onPassed?.(trimmedCode);
      onClose?.();
    } catch (error) {
      setErrorText(error?.message || "Verification failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth='xs'
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 4,
          boxShadow: "0px 16px 40px rgba(0, 0, 0, 0.12)",
          // 默认边框使用警告色，悬停时加深
          border: "2px solid",
          borderColor: "warning.light",
          transition: "border-color 0.3s",
          "&:hover": {
            borderColor: "warning.main", // 悬停整个弹窗边框变警告色
          },
        },
      }}
    >
      <Box
        component='form'
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        {/* 顶部标题区：改为简洁的白色背景 + 顶部警告色强调条 */}
        <DialogTitle
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            pt: 4,
            pb: 2,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "warning.dark",
            }}
          >
            <WarningAmberIcon
              sx={{ fontSize: "2.2rem", color: "warning.dark", mr: 2 }}
            />
            <Typography
              variant='h6'
              sx={{ fontWeight: 800, letterSpacing: 0.5 }}
            >
              Danger Zone
            </Typography>
          </Box>

          <Typography
            variant='body2'
            sx={{
              color: "text.secondary",
              fontWeight: 500,
              mt: 0.5,
            }}
          >
            High-Risk Security Action
          </Typography>
        </DialogTitle>

        <DialogContent
          sx={{
            pt: 1,
            pb: 2,
            px: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {/* 警告提示文案 */}
          <Alert
            variant='filled' // 使用填充型，视觉更强
            severity='warning' // 统一使用 warning 色调
            sx={{
              mb: 3,
              borderRadius: 2,
              fontWeight: 500,
              width: "100%",
              textAlign: "center",
              boxShadow: "none",
              background: alpha(theme.palette.warning.main, 0.15), // 柔和的警告色背景
              color: theme.palette.warning.dark,
            }}
          >
            This operation is irreversible. Double-check all details before
            proceeding.
          </Alert>

          <Box sx={{ mb: 3, width: "100%", maxWidth: "340px" }}>{details}</Box>

          <Typography
            variant='body2'
            color='text.secondary'
            align='center'
            sx={{ mb: 3, maxWidth: "320px", lineHeight: 1.6 }}
          >
            Enter 6-digit security code to continue.
          </Typography>

          {/* 验证码输入框 */}
          <TextField
            label='Verification Code'
            value={verificationCode}
            onChange={(e) =>
              setVerificationCode(e.target.value.replace(/\D/g, ""))
            }
            fullWidth
            variant='outlined'
            placeholder='000000'
            autoFocus
            error={Boolean(errorText)}
            helperText={errorText || "Enter the 6-digit code to confirm."}
            autoComplete='off'
            inputProps={{
              maxLength: 6,
              inputMode: "numeric",
              autoComplete: "off",
              style: {
                textAlign: "center",
                fontSize: "1.6rem",
                fontWeight: "bold",
                letterSpacing: "0.3rem",
                padding: "14px",
                color: theme.palette.warning.dark,
              },
            }}
            sx={{
              maxWidth: "280px",
              "& .MuiOutlinedInput-root": {
                borderRadius: 3,
                bgcolor: "#fff",
                fontWeight: 700,
                "& fieldset": { borderColor: theme.palette.warning.main },
                "&:hover fieldset": {
                  borderColor: theme.palette.warning.dark,
                  borderWidth: 2,
                },
                "&.Mui-focused fieldset": {
                  borderColor: theme.palette.warning.dark,
                  borderWidth: 2,
                },
                "&.Mui-error fieldset": {
                  borderColor: theme.palette.error.main,
                },
              },
              "& .MuiInputLabel-root": { color: theme.palette.warning.main },
              "& .Mui-focused .MuiInputLabel-root": {
                color: theme.palette.warning.dark,
              },
              "& .MuiFormHelperText-root": {
                color: "text.secondary",
                fontWeight: 500,
              },
            }}
          />
        </DialogContent>

        <Divider
          sx={{ mx: 4, borderColor: alpha(theme.palette.warning.main, 0.2) }}
        />

        <DialogActions sx={{ justifyContent: "center", p: 3, gap: 2 }}>
          <Button
            onClick={onClose}
            disabled={submitting}
            variant='outlined'
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              px: 3,
              color: "text.secondary",
              height: 48,
              borderColor: "divider",
              // 取消按钮 Hover 变为警告色
              "&:hover": {
                borderColor: "warning.main",
                color: "warning.dark",
                bgcolor: alpha(theme.palette.warning.main, 0.08),
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            type='submit'
            disabled={!canSubmit}
            sx={{
              bgcolor: "warning.main",
              color: "#fff",
              minWidth: 160,
              borderRadius: 2,
              textTransform: "none",
              fontSize: "1rem",
              fontWeight: 700,
              height: 48,
              boxShadow: `0 4px 14px ${alpha(theme.palette.warning.main, 0.4)}`,
              // 确认按钮 Hover 加深警告色
              "&:hover": {
                bgcolor: "warning.dark",
                boxShadow: `0 6px 18px ${alpha(theme.palette.warning.dark, 0.5)}`,
              },
              "&.Mui-disabled": {
                bgcolor: alpha(theme.palette.warning.main, 0.3),
                color: "#fff",
              },
            }}
          >
            {submitting ? (
              <CircularProgress size={22} color='inherit' />
            ) : (
              "Confirm to continue"
            )}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default SecurityGuard;
